import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    CircularProgress,
    Avatar,
    InputAdornment,
    Alert
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    QrCode as QrCodeIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import * as QRCampaignsService from '../../services/qrcampaigns.service';
import {formatCurrency} from '../../utils/Functions';

const QRCampaignForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {id} = useParams();
    const {requesting} = useSelector((state) => state.Process);

    const isEdit = Boolean(id);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        campaignname: '',
        description: '',
        amount: '',
        expiration_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
        campaign_type: 'DONATION',
        max_uses: 0
    });

    const [campaignData, setCampaignData] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isEdit) {
            loadCampaign();
        }
    }, [id, isEdit]);

    const loadCampaign = async () => {
        setLoading(true);
        try {
            const response = await QRCampaignsService.getSingleQRCampaign(dispatch, {id: parseInt(id)});
            if (response && response.status && response.result.length > 0) {
                const campaign = response.result[0];
                setCampaignData(campaign);
                setFormData({
                    campaignname: campaign.CAMPAIGNNAME || '',
                    description: campaign.DESCRIPTION || '',
                    amount: campaign.AMOUNT || '',
                    expiration_date: new Date(campaign.EXPIRATION_DATE),
                    campaign_type: campaign.CAMPAIGN_TYPE || 'DONATION',
                    max_uses: campaign.MAX_USES || 0
                });
            }
        } catch (error) {
            console.error('Error loading campaign:', error);
        }
        setLoading(false);
    };

    const handleInputChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value
        });
        if (errors[field]) {
            setErrors({
                ...errors,
                [field]: null
            });
        }
    };

    const handleDateChange = (event) => {
        const date = new Date(event.target.value);
        setFormData({
            ...formData,
            expiration_date: date
        });
        if (errors.expiration_date) {
            setErrors({
                ...errors,
                expiration_date: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.campaignname.trim()) {
            newErrors.campaignname = 'El nombre de la campaña es requerido';
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'El monto debe ser mayor a 0';
        }

        if (!formData.expiration_date || formData.expiration_date <= new Date()) {
            newErrors.expiration_date = 'La fecha de expiración debe ser futura';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSaving(true);
        try {
            const submitData = {
                ...formData,
                amount: parseFloat(formData.amount),
                expiration_date: formData.expiration_date.toISOString().slice(0, 19).replace('T', ' '),
                max_uses: 0  // Always set to 0 for unlimited uses
            };

            if (isEdit) {
                submitData.id = parseInt(id);
            }

            const service = isEdit ? QRCampaignsService.updateQRCampaign : QRCampaignsService.createQRCampaign;
            const response = await service(dispatch, submitData);

            if (response && response.status) {
                navigate(process.env.REACT_APP_ADMMIN_PATH + '/manage/qrcampaigns');
            }
        } catch (error) {
            console.error('Error saving campaign:', error);
        }
        setSaving(false);
    };

    const handleCancel = () => {
        navigate(process.env.REACT_APP_ADMMIN_PATH + '/manage/qrcampaigns');
    };

    const handleDownloadQR = () => {
        if (campaignData && campaignData.QR_IMAGE) {
            const link = document.createElement('a');
            link.href = `data:image/png;base64,${campaignData.QR_IMAGE}`;
            link.download = `QR_${campaignData.CAMPAIGNNAME}.png`;
            link.click();
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                {isEdit ? 'Editar Campaña QR' : 'Nueva Campaña QR'}
            </Typography>

            {isEdit && campaignData && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>Nota:</strong> Al editar una campaña, el código QR no cambiará.
                    Solo se actualizarán los datos de la campaña.
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Campaign Details */}
                        <Grid item xs={12} md={8}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Información de la Campaña
                                    </Typography>

                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Nombre de la Campaña"
                                                value={formData.campaignname}
                                                onChange={handleInputChange('campaignname')}
                                                error={!!errors.campaignname}
                                                helperText={errors.campaignname}
                                                required
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Descripción"
                                                multiline
                                                rows={3}
                                                value={formData.description}
                                                onChange={handleInputChange('description')}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Monto por Donación"
                                                type="number"
                                                value={formData.amount}
                                                onChange={handleInputChange('amount')}
                                                error={!!errors.amount}
                                                helperText={errors.amount}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">Bs.</InputAdornment>,
                                                }}
                                                required
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Tipo de Campaña</InputLabel>
                                                <Select
                                                    value={formData.campaign_type}
                                                    onChange={handleInputChange('campaign_type')}
                                                    label="Tipo de Campaña"
                                                >
                                                    <MenuItem value="DONATION">Donación</MenuItem>
                                                    <MenuItem value="FUNDRAISING">Recaudación</MenuItem>
                                                    <MenuItem value="EVENT">Evento</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Fecha de Expiración"
                                                type="datetime-local"
                                                value={formData.expiration_date.toISOString().slice(0, 16)}
                                                onChange={handleDateChange}
                                                error={!!errors.expiration_date}
                                                helperText={errors.expiration_date}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    min: new Date().toISOString().slice(0, 16)
                                                }}
                                                required
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* QR Code Preview */}
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {isEdit ? 'Código QR Actual' : 'Vista Previa'}
                                    </Typography>

                                    {isEdit && campaignData && campaignData.QR_IMAGE ? (
                                        <Box textAlign="center">
                                            <Avatar
                                                src={`data:image/png;base64,${campaignData.QR_IMAGE}`}
                                                variant="square"
                                                sx={{ width: 200, height: 200, margin: '0 auto', mb: 2 }}
                                            />
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                QR ID: {campaignData.QR_ID}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                startIcon={<DownloadIcon />}
                                                onClick={handleDownloadQR}
                                                fullWidth
                                            >
                                                Descargar QR
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box textAlign="center" py={4}>
                                            <QrCodeIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                                            <Typography variant="body2" color="textSecondary">
                                                El código QR se generará después de crear la campaña
                                            </Typography>
                                        </Box>
                                    )}

                                    {isEdit && campaignData && (
                                        <Box mt={3}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Estadísticas
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Usos:</strong> {campaignData.CURRENT_USES} / {campaignData.MAX_USES === 0 ? '∞' : campaignData.MAX_USES}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Recaudado:</strong> {formatCurrency(campaignData.TOTAL_COLLECTED || 0, 'BOB')}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Estado:</strong> {campaignData.STATUS}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box display="flex" gap={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    disabled={saving}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                    disabled={saving}
                                    sx={{ backgroundColor: '#009ce0' }}
                                >
                                    {saving ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear Campaña')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
        </Box>
    );
};

export default QRCampaignForm;